import { types, getParentOfType, getParent, Instance } from 'mobx-state-tree'
import { IProject } from 'src/api/models/Project/types'
import { ContentEdge } from 'src/api/models/Content/ContentEdge'
import { IContentEdge } from 'src/api/models/Content/ContentEdge/types'
import {
  generateNodeType,
  generateType
} from 'src/api/models/utils/generateNodeType'
import { generateBracketShape } from 'src/vectorPaths/bracket'
import { Side } from 'src/api/models/values'
import {
  fastener,
  fastenerEdge,
  fastenerConnection,
  fastenerComponentConnection,
  brackets,
  bracketsEdge,
  bracketsEdgeProperties,
  distanceBolts,
  distanceBoltsEdge,
  printedcircuitboard
} from 'src/constants/typenames'
import { top, right, bottom, left } from 'src/constants/sides'
import {
  IBracketsEdgeProperties,
  IBracketsEdge,
  IFastenerComponentConnection
} from './types'
import {
  IAdapterHeadEdge,
  IAdapterPlateEdge,
  IAdapter
} from 'src/api/models/Enclosure/Adapter/types'

export const Brackets = generateType(brackets)

const BracketsEdgeProperties: IBracketsEdgeProperties = generateType(
  bracketsEdgeProperties
)
  .props({
    side: types.optional(types.enumeration([top, bottom]), bottom)
  })
  .extend((self) => {
    /** 8. Project 7. FastenerConnection  6. types.array(FastenerEdge)  5. FastenerEdge  4. Fastener  3. FastenerComponentConnection  2. types.array(BracketsEdge)  1. BracketsEdge */
    const project = getParent<Instance<IProject>>(self, 8) //getParentOfType(self, Project)

    const fastener = getParentOfType(self, Fastener)
    return {
      views: {
        get shapes() {
          const props = {
            properties: fastener.content.properties,
            node: fastener.content.node,
            side: self.side
          }
          return {
            left: generateBracketShape({ ...props, globalPlateSide: left }),
            right: generateBracketShape({ ...props, globalPlateSide: right })
          }
        },
        get placements() {
          const leftHeads: Instance<IAdapterHeadEdge>[] = []
          const rightHeads: Instance<IAdapterHeadEdge>[] = []
          project?.enclosure.adapterEdges[0].node.plates.edges
            ?.find?.(
              (edge: Instance<IAdapterPlateEdge>) =>
                edge.properties.side === self.side
            )
            ?.node.heads.edges.forEach((edge: Instance<IAdapterHeadEdge>) => {
              if (edge.properties.globalSide === right) {
                rightHeads.push(edge)
              } else if (edge.properties.globalSide === left) {
                leftHeads.push(edge)
              }
            })
          return {
            left: leftHeads.reduce(
              (prev, headEdge) => [...prev, ...headEdge.properties.brackets],
              [] as [number, number, number][]
            ),
            right: rightHeads.reduce(
              (prev, headEdge) => [...prev, ...headEdge.properties.brackets],
              [] as [number, number, number][]
            )
          }
        }
      }
    }
  })

export const BracketsEdge: IBracketsEdge = generateType(bracketsEdge).props({
  properties: types.optional(BracketsEdgeProperties, {} as any),
  node: types.optional(Brackets, {})
})

const DistanceBolt = generateType(distanceBolts)
const DistanceBoltEdge = generateType(distanceBoltsEdge)

const FastenerComponentConnection: IFastenerComponentConnection = generateType(
  fastenerComponentConnection
)
  .props({
    edges: types.optional(types.array(types.union(BracketsEdge)), [])
  })
  .actions((self) => {
    return {
      afterAttach() {
        if (self.edges.length === 0) {
          const fastener = getParentOfType(self, Fastener)
          if (fastener.type === 'Bracket') {
            self.edges.push(BracketsEdge.create())
          }
        }
      }
    }
  })
  .views((self) => {
    return {
      get brackets() {
        return self.edges.filter((edge) => edge.__typename === bracketsEdge)
      }
    }
  })

export const Fastener = generateNodeType(fastener).props({
  content: types.reference(ContentEdge),
  type: types.optional(
    types.enumeration(['Bracket', 'DistanceBolt']),
    'Bracket'
  ),
  components: types.optional(FastenerComponentConnection, {} as any)
})

export const FastenerEdge = generateType(fastenerEdge).props({
  node: Fastener
})

const bracketsOption = 'brackets' as const
const distanceBoltsOption = 'distanceBolts' as const

const fastenerOptions = [bracketsOption, distanceBoltsOption]

export const FastenerConnection = generateNodeType(fastenerConnection)
  .props({
    value: types.optional(types.enumeration(fastenerOptions), bracketsOption),
    edges: types.optional(types.array(FastenerEdge), [])
  })
  .views((self) => ({
    get type() {
      return 'radio'
    },
    get displayValue() {
      return self.value
    },
    get options() {
      return fastenerOptions
    }
  }))
  .actions((self) => {
    return {
      afterAttach() {
        if (self.edges.length === 0) {
          const project = getParent<Instance<IProject>>(self, 1) //getParentOfType(self, Project)
          project.content.edges.forEach((edge: Instance<IContentEdge>) => {
            if (edge.node.__typename === printedcircuitboard) {
              self.edges.push(
                FastenerEdge.create({ node: { content: edge.id } })
              )
            }
          })
        }
      },
      update(option: Unpacked<typeof fastenerOptions>) {
        self.value = option

        /** CONVERT ContentEdge Gap Left Right */
        if (option === 'distanceBolts') {
          const project = getParent<IProject>(self)
          // const nextLeftGap = GapSide.create({})
          // project.
        }
      }
    }
  })
